import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Music2, FileText } from "lucide-react";
import { apiFetch } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles, getSimulatedIsMusician } from "./roleSimulator";
import { cifraUrlComTom } from "./musicKeys";

// Tela de leitura do repertorio, pra equipe de louvor (role MUSICO) estudar
// as musicas antes do ensaio/culto -- ver AdminRepertorio.jsx pra quem
// gerencia o catalogo (ADMIN/MUSICO -- so MUSICO mesmo, pedido do Rodrigo).

const HOME_PATH =
  typeof window !== "undefined" &&
  (window.location.hostname.startsWith("familia.") || window.location.hostname === "localhost")
    ? "/"
    : "/familia";

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const linkButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  fontWeight: 800,
  textDecoration: "none",
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.28)"
};

// Mesma logica de AdminRepertorio.jsx -- "2026-08-02" (ou com hora) vira
// "02/08/2026", pro selo "Tocada em" no card do musico.
function formatLastPlayed(value) {
  if (!value) return null;
  const iso = String(value).slice(0, 10);
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return null;
  return `${d}/${m}/${y}`;
}

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={HOME_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

export default function RepertorioMusico() {
  const [status, setStatus] = useState("checking");
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const roles = getEffectiveRoles(me);
        // Capacidade de musico mora na Person (isMusician), nao em
        // User.roles -- mesmo motivo/correcao de AdminEscalas.jsx
        // (20/10/2026, caso do Gabriel).
        const realIsAdminUser = Boolean(me?.roles?.includes("ADMIN"));
        const isMusicianByPerson = getSimulatedIsMusician(realIsAdminUser, me?.person?.isMusician);
        const allowed = roles.includes("ADMIN") || roles.includes("MUSICO") || isMusicianByPerson;
        if (allowed && !me.passwordMustChange) {
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (status !== "ready") return;
    let alive = true;
    setLoading(true);
    apiFetch("/songs", { method: "GET" })
      .then((data) => {
        if (alive) setSongs(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (alive) setFeedback("Não foi possível carregar o repertório agora.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [status]);

  if (status === "checking") return <CenteredMessage>Carregando…</CenteredMessage>;
  if (status === "forbidden") return <CenteredMessage>Você não tem acesso a esta área.</CenteredMessage>;

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <Link to={HOME_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <h1 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900, display: "flex", alignItems: "center", gap: 8 }}>
          <Music2 size={20} /> Repertório
        </h1>
        <p style={{ margin: "0 0 20px", color: "#9fb0cc", fontSize: 13 }}>
          Músicas pra estudar antes do ensaio ou do culto -- toque no link pra ouvir a referência.
        </p>

        {feedback && <div style={{ ...cardStyle, marginBottom: 16, color: "#FCA5A5", fontSize: 13 }}>{feedback}</div>}
        {loading && <p style={{ color: "#9fb0cc", fontSize: 13 }}>Carregando…</p>}

        {!loading && songs.length === 0 && !feedback && (
          <div style={{ ...cardStyle, textAlign: "center", color: "#9fb0cc", fontSize: 13 }}>
            Nenhuma música no repertório por enquanto.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {songs.map((song) => (
            <div key={song.id} style={cardStyle}>
              <strong style={{ fontSize: 15 }}>{song.title}</strong>
              <div style={{ marginTop: 4, fontSize: 13, color: "#9fb0cc" }}>
                {[song.artist, song.key ? `Tom ${song.key}` : null, song.bpm ? `${song.bpm} bpm` : null]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </div>
              {formatLastPlayed(song.lastPlayedAt) && (
                <div style={{ marginTop: 4, fontSize: 11, color: "#6b7a99" }}>
                  Tocada em {formatLastPlayed(song.lastPlayedAt)}
                </div>
              )}
              {song.notes && (
                <p style={{ margin: "8px 0 0", fontSize: 13, color: "#c7ced9", lineHeight: 1.5 }}>{song.notes}</p>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {song.referenceUrl && (
                  <a href={song.referenceUrl} target="_blank" rel="noreferrer" style={{ ...linkButtonStyle, color: "#7DD3FC", borderColor: "rgba(125,211,252,0.35)" }}>
                    <ExternalLink size={12} /> Ouvir referência
                  </a>
                )}
                {song.lyricsUrl && (
                  <a href={cifraUrlComTom(song.lyricsUrl, song.key)} target="_blank" rel="noreferrer" style={{ ...linkButtonStyle, color: "#c7ced9" }}>
                    <FileText size={12} /> Cifra/letra{song.key ? ` (tom ${song.key})` : ""}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
